import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';

// iter947 — shared loader for the tenant-wide "Pending Cross-Branch Approvals"
// count + list. Powers the amber badge on the Banking Hub tile / sidebar and
// the Reconciliation Center bulk-approve card. Reuses the persisted
// cross_branch_suggestions from the recon runs — no new math.
//
// iter — shared in-flight dedupe + short cache. The hook is mounted in several
// places at once (DashboardLayout, Hub tile, dashboard alert card, Recon
// Center). Previously each mount fired its OWN GET /pending-offsets on load,
// so a single dashboard render triggered 2-3 identical calls. We now coalesce
// concurrent callers onto ONE request and reuse the result for a few seconds,
// while preserving each caller's refresh/poll behaviour.
const CACHE_TTL_MS = 10000; // reuse a fetched result for 10s across mounts
let _cache = { ts: 0, count: 0, suggestions: [] };
let _inflight = null;

async function _loadPendingOffsets(force = false) {
  const now = Date.now();
  if (!force && _inflight) return _inflight;                 // join the in-flight request
  if (!force && now - _cache.ts < CACHE_TTL_MS) return _cache; // serve fresh cache
  _inflight = (async () => {
    try {
      const r = await api.get('/accounting/reconciliation/pending-offsets');
      _cache = {
        ts: Date.now(),
        count: r.data?.count || 0,
        suggestions: r.data?.suggestions || [],
      };
      return _cache;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

export function usePendingOffsets(pollMs = 0, enabled = true) {
  const [count, setCount] = useState(_cache.count);
  const [suggestions, setSuggestions] = useState(_cache.suggestions);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async (force = false) => {
    if (!enabled) return;  // caller lacks reports:read — never hit the protected endpoint
    setLoading(true);
    try {
      const data = await _loadPendingOffsets(force);
      setCount(data.count || 0);
      setSuggestions(data.suggestions || []);
    } catch {
      /* non-blocking */
    }
    setLoading(false);
  }, [enabled]);

  useEffect(() => {
    let cancelled = false;
    if (!enabled) { setCount(0); setSuggestions([]); return; }
    refresh();
    if (pollMs > 0) {
      const id = setInterval(() => { if (!cancelled && !document.hidden) refresh(true); }, pollMs);
      return () => { cancelled = true; clearInterval(id); };
    }
    return () => { cancelled = true; };
  }, [refresh, pollMs, enabled]);

  return { count, suggestions, loading, refresh };
}
